// Every piece of visible Web MMS text, in one place (H §4). Feature modules and the shell read it through
// useCopy(); no component carries its own wording. Terminology is provisional until Webstercare's terminology
// write-up lands, when it changes here in a single pass. On screen we say "client"; code says patient or client.
// Australian English.

const dash = '—';

export const en = {
  product: {
    name: 'Web MMS',
    owner: 'Webstercare',
  },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    none: dash,
    notAvailableTitle: 'Not available yet',
    notAvailableBody: 'This part of the Web MMS is not switched on yet. It will appear here once it is.',
    errorTitle: 'Something went wrong',
    errorBody: 'The information could not be loaded. Try again, or contact support if it keeps happening.',
    errorReference: (reference: string) => `Support reference: ${reference}`,
    networkError: 'The Web MMS could not be reached. Check your connection and try again.',
  },
  shell: {
    skipToContent: 'Skip to content',
    navigation: 'Main navigation',
    groups: { clinical: 'Clinical', logistics: 'Logistics', business: 'Business' },
    signOut: 'Sign out',
    signedInAs: (name: string) => `Signed in as ${name}`,
    loadingSession: 'Loading your pharmacy…',
    sessionErrorTitle: 'Your account could not be loaded',
    sessionErrorBody:
      'The Web MMS could not confirm your role and pharmacy. Try again, or sign out and back in.',
    forbiddenTitle: 'You do not have access to this page',
    forbiddenBody: 'Your role does not include this part of the Web MMS. Ask your pharmacy administrator.',
    notFoundTitle: 'Page not found',
    notFoundBody: 'The page you asked for does not exist.',
    goHome: 'Go to the dashboard',
  },
  signIn: {
    title: 'Sign in to the Web MMS',
    developmentHeading: 'Development sign-in',
    developmentHelp:
      'Local development only. Paste a token from the development token issuer, or set VITE_DEV_TOKEN in .env.local.',
    tokenLabel: 'Development token',
    submit: 'Sign in',
    malformed: 'That is not a valid token.',
    expired: 'That token has expired. Issue a new one.',
    rejected: 'Your session has ended. Sign in again.',
    // The placeholder email-and-password sign-in for the Development environment, until the identity provider.
    password: {
      heading: 'Sign in',
      intro: 'Use your Web MMS account.',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      invalidCredentials: 'The email or password is incorrect.',
      throttled: 'Too many attempts. Wait a few minutes, then try again.',
      unavailable: 'Sign-in is not available right now. Try again shortly.',
      environmentNote: 'Development sign-in',
    },
    unconfiguredTitle: 'Sign-in is not configured',
    unconfiguredBody:
      'This build of the Web MMS has no sign-in method configured. Contact the platform team.',
  },
  nav: {
    dashboard: 'Dashboard',
    clients: 'Clients',
    activateMcm: 'Activate MCM',
  },
  adherence: {
    status: {
      onTime: 'On time',
      offSchedule: 'Off schedule',
      missed: 'Missed',
      pending: 'Not yet due',
      noData: 'No data',
    },
    timing: { early: 'Early', late: 'Late' },
    recordSource: {
      device: 'MCM device',
      carer: 'Carer, recorded in the app',
      'subject-of-care': 'Client, recorded in the app',
    },
    onTimePercent: (percent: number) => `${percent}% on time`,
    windowCaption: (days: number) => `${days}d`,
    summary: (onTime: number, offSchedule: number, missed: number) =>
      `${onTime} on time, ${offSchedule} off schedule, ${missed} missed`,
    noObservedDoses: 'No observed doses',
    notMonitored: 'Not monitored',
  },
  mcm: {
    status: { notActivated: 'Not activated', pending: 'Activation pending', active: 'Active' },
    connectivity: { online: 'MCM online', offline: 'MCM offline', unknown: 'MCM status unknown' },
  },
  attention: {
    needsAttention: 'Needs attention',
    deviceOffline: 'MCM offline',
    noPackLoaded: 'No Webster-pak loaded',
    packNotPrinted: 'Webster-pak not printed',
    packChangeDue: 'Webster-pak change due',
    missedDoses: 'Missed in the last 24 hours',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: (pharmacy: string) => `MCM clients at ${pharmacy}`,
    tilesLabel: 'Clients by attention state',
    tileHint: 'Select to list these clients',
    tilesNotAvailable: 'Attention counts are not available yet.',
    pharmacyAdherence: (days: number) => `Pharmacy adherence, last ${days} days`,
    listHeading: 'Clients',
    attentionFilter: (label: string) => `Showing: ${label}`,
    clearFilter: 'Clear filter',
    filterNotAvailable: 'Filtering by attention state is not available yet.',
    viewAllClients: 'View all clients',
    columns: {
      client: 'Client name',
      group: 'Group',
      adherence: (days: number) => `Adherence, last ${days} days`,
      attention: 'Needs attention',
      nextPack: 'Next Webster-pak',
    },
    noneScheduled: 'None scheduled',
    empty: 'No clients to show.',
  },
  clients: {
    title: 'Clients',
    searchLabel: 'Search clients',
    searchPlaceholder: 'Name, Medicare, NDIS or concession number',
    searchTooShort: 'Type at least two characters to search.',
    mcmFilterLabel: 'MCM status',
    mcmFilterAll: 'All MCM statuses',
    filterNotAvailable: 'Filtering by MCM status is not available yet. Showing no results for this filter.',
    columns: {
      client: 'Client name',
      dateOfBirth: 'Date of birth',
      medicare: 'Medicare',
      group: 'Group',
      mcm: 'MCM',
      adherence: 'Adherence',
    },
    empty: 'No clients found matching your search.',
    loadMore: 'Load more clients',
    loadingMore: 'Loading more…',
    endOfList: (count: number) => (count === 1 ? 'Showing 1 client' : `Showing all ${count} clients`),
    shownCount: (count: number) => `Showing ${count} ${count === 1 ? 'client' : 'clients'}`,
    backToList: 'Back to clients',
    notFoundTitle: 'Client not found',
    notFoundBody: 'There is no client with this identifier in your pharmacy.',
  },
  client: {
    tabs: {
      overview: 'Overview',
      medications: 'Medication profile',
      adherence: 'MCM adherence',
      careTeam: 'Care team',
    },
    preferred: (name: string) => `Prefers ${name}`,
    serial: (serial: string) => `MCM ${serial}`,
    lastPackChange: 'Last Webster-pak change',
    nextPack: 'Next Webster-pak',
    allergiesHeading: (count: number) =>
      `Allergies and adverse reactions (${count}), review before dispensing`,
    allergyKind: { drug: 'Drug', nonDrug: 'Non-drug' },
    noKnownAllergies: 'No known allergies recorded.',
    details: 'Details',
    identifiersHeading: 'Identifiers',
    clinicalHeading: 'Clinical context',
    fields: {
      dateOfBirth: 'Date of birth',
      age: 'Age',
      gender: 'Gender',
      mobile: 'Mobile',
      address: 'Address',
      group: 'Group',
      ihi: 'IHI',
      ihiStatus: (status: string) => `(${status})`,
      medicare: 'Medicare',
      irn: (irn: string) => `IRN ${irn}`,
      concessionCard: 'Concession card',
      dva: 'DVA',
      ndis: 'NDIS',
      conditions: 'Conditions',
      prescriber: 'Prescriber',
      consent: 'Consent',
      clinicalNote: 'Clinical note',
    },
    gender: { female: 'Female', male: 'Male', other: 'Other', unknown: 'Not stated' },
    consentType: { client: 'Client', guardian: 'Guardian', informed: 'Informed consent' },
    consentDetail: (by: string | null, from: string | null) =>
      [from && `from ${from}`, by && `obtained by ${by}`].filter(Boolean).join(', '),
    readOnlyNote: 'Read-only. Client details are maintained in the pharmacy’s MMS.',
  },
  adherenceWeek: {
    heading: 'Webster-pak adherence',
    weekRange: (from: string, to: string) => `${from} – ${to}`,
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    legend: 'Legend',
    totals: 'This week',
    notAvailable: 'The adherence grid is not available yet.',
    noDevice: 'This client has no active MCM, so there is no adherence to show.',
    cellLabel: (day: string, slot: string, status: string) => `${day}, ${slot}: ${status}`,
    selectHint: 'Select a dose to see its detail.',
    detail: {
      doseWindow: 'Dose window',
      due: (time: string) => `Due ${time}`,
      recorded: 'Recorded',
      recordedAt: (time: string) => `at ${time}`,
      recordedBetween: (from: string, to: string) => `between ${from} and ${to}`,
      recordedBy: (to: string) => `by ${to}`,
      notRecorded: 'Not recorded',
      windowOnlyNote:
        'Exact time isn’t recorded. The MCM logs the window in which the Webster-pak was out of the dock.',
      noDataNote: 'The MCM was not reporting during this window, so the dose could not be observed.',
      pendingNote: 'The window has not ended yet.',
      source: 'Recorded by',
      observations: 'Observations',
      noObservations: 'No observations for this dose.',
      observedBy: (name: string) => name,
      medications: 'Medication in this dose',
      noMedications: 'No medication listed for this dose.',
      close: 'Close dose detail',
    },
  },
  careTeam: {
    carersHeading: 'Carers',
    noCarers: 'No carers linked to this client.',
    viewOnly: 'Carers are added when the MCM is activated. Changes after activation are not available yet.',
    relationship: {
      son: 'Son',
      daughter: 'Daughter',
      partnerSpouse: 'Partner or spouse',
      sibling: 'Sibling',
      parent: 'Parent',
      friend: 'Friend',
      neighbour: 'Neighbour',
      other: 'Other',
    },
    invitation: {
      queued: 'Invitation queued',
      sent: 'Invitation sent',
      delivered: 'Invitation delivered',
      accepted: 'Using the app',
      failed: 'Invitation failed',
    },
    clientAppUser: 'Client app user',
    clientAppUserNone: 'The client is not registered as an app user.',
    prescriberHeading: 'Prescriber',
    pharmacyHeading: 'Pharmacy',
    notAvailable: 'The care team is not available yet.',
  },
  noDoseRecords: {
    title: 'No doses recorded yet',
    body: 'Adherence appears here once the MCM reports the first dose from this client’s Webster-pak.',
  },
  adherenceTrend: {
    heading: 'Adherence trend',
    groupByLabel: 'Group by',
    groupBy: { day: 'Day', week: 'Week', month: 'Month' },
    periodNoun: { day: 'day', week: 'week', month: 'month' },
    latest: (period: string) => `On time, last full ${period}`,
    change: (points: number, period: string) =>
      points === 0
        ? `No change on the previous ${period}`
        : `${points > 0 ? 'Up' : 'Down'} ${Math.abs(points)} points on the previous ${period}`,
    range: (from: string, to: string) => `${from} – ${to}`,
    chartLabel: (period: string) => `Share of doses on time, off schedule and missed, by ${period}`,
    onTimeLine: 'On time %',
    inProgress: 'In progress',
    inProgressNote:
      'The latest period is still in progress, so it is shown faded and left off the trend line.',
    doses: (count: number) => `${count} ${count === 1 ? 'dose' : 'doses'}`,
    onTimePercent: (percent: number | null) => (percent === null ? '—' : `${percent}%`),
    noDataCount: (count: number) => `${count} with no data`,
    mostMissed: 'Missed',
    mostOffSchedule: 'Off schedule',
    noPoints: 'No doses fall in this range.',
    notAvailable: 'The adherence trend is not available yet.',
    tableCaption: 'Adherence by period',
    columns: {
      period: 'Period',
      doses: 'Doses',
      onTime: 'On time',
      offSchedule: 'Off schedule',
      missed: 'Missed',
      percent: 'On time %',
    },
    showTable: 'Show as table',
    hideTable: 'Hide table',
  },
  medicationProfile: {
    readOnlyTitle: 'Read-only',
    readOnlyBody: (date: string, time: string) =>
      `Maintained in the pharmacy’s MMS and cannot be changed here. Last updated ${date} at ${time}.`,
    packHeading: (number: string, name: string | null) =>
      name ? `Pack ${number} · ${name}` : `Pack ${number}`,
    notPackedHeading: 'Not in a pack',
    inMcm: 'In the MCM',
    nextCycle: (date: string) => `Next cycle ${date}`,
    checkStatus: (status: string) => status,
    medicineCount: (count: number) => `${count} ${count === 1 ? 'medicine' : 'medicines'}`,
    columns: {
      medicine: 'Medicine',
      dose: 'Dose',
      prescriber: 'Prescriber',
      started: 'Started',
    },
    slot: { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', bedtime: 'Bedtime' },
    slotQuantity: (slot: string, quantity: string) => `${slot}: ${quantity}`,
    notInSlot: (slot: string) => `${slot}: none`,
    ends: (date: string) => `Ends ${date}`,
    empty: 'No medicines are recorded for this client in the pharmacy’s MMS.',
    notFound: 'The pharmacy’s MMS holds no medication profile for this client yet.',
    notAvailable: 'The medication profile is not available yet.',
    doseWindowsHeading: 'Dose windows',
    doseWindowsIntro: (pack: string) =>
      `When each dose in pack ${pack} counts as on time. Set from the pharmacy’s slot times; read-only.`,
    due: (time: string) => `Due ${time}`,
    window: (from: string, to: string) => `${from} – ${to}`,
    doseWindowsNotFound: 'This client has no medicines in pack 1, so there are no dose windows.',
    doseWindowsNotAvailable: 'Dose windows are not available yet.',
  },
  activation: {
    cancel: 'Cancel',
    cancelConfirm: 'Cancel this activation? Nothing has been saved yet.',
    cancelDiscard: 'Yes, cancel',
    cancelKeep: 'Keep going',
    back: 'Back',
    continue: 'Continue',
    change: 'Change',
    steps: { client: 'Find client', device: 'Link device', carers: 'Carers', review: 'Review' },
    progress: 'Activation progress',
    stepOf: (step: number, total: number, label: string) => `Step ${step} of ${total}: ${label}`,
    stepDone: (label: string) => `${label}, done`,
    client: {
      title: 'Find client',
      intro:
        'Search for the client in the register. They must already be registered in the pharmacy’s MMS, and only clients without an MCM are listed.',
      searchLabel: 'Search clients without an MCM',
      searchPlaceholder: 'Name, Medicare, NDIS or concession number',
      searchTooShort: 'Type at least two characters to search.',
      results: (count: number) => `${count} ${count === 1 ? 'client' : 'clients'}`,
      moreResults: 'More clients match. Refine the search to narrow the list.',
      empty: 'No clients without an MCM match this search. Try a different name or card number.',
      detail: (dob: string, mobile: string, medicare: string) =>
        `DOB ${dob} · Mobile ${mobile} · Medicare ${medicare}`,
      selected: 'Selected',
      loadingPrefill: 'Loading the client…',
      prefillNotFoundTitle: 'Client not found',
      prefillNotFoundBody: 'There is no client with this identifier in your pharmacy.',
      alreadyActivatedTitle: (name: string) => `${name} already has an MCM`,
      alreadyActivatedBody:
        'Activation is only for clients without an MCM. To change a client’s device, use Settings.',
      viewRecord: 'View client record',
      chooseAnother: 'Choose another client',
    },
    device: {
      title: 'Link MCM device',
      intro:
        'Scan the barcode with your counter scanner, or type the serial number printed on the unit. Or choose a device from your pharmacy’s stock.',
      serialLabel: 'Device serial number',
      serialPlaceholder: 'e.g. MCM-2026-01234',
      serialHint: 'The barcode is on the underside of the MCM and on the box.',
      serialInvalid: 'A serial number is 4 to 32 letters, digits or hyphens.',
      find: 'Find device',
      checking: 'Checking the device…',
      notFound: (serial: string) =>
        `No device with serial ${serial} is held by your pharmacy. Check the number and try again.`,
      valid: (serial: string) => `${serial}: valid device`,
      notEligible: (serial: string, reason: string) => `${serial} can’t be activated: ${reason}`,
      reason: {
        alreadyAssigned: 'it is already linked to another client.',
        notAllocatedToPharmacy: 'it is not allocated to your pharmacy.',
        notPaired: 'it has not been paired yet.',
        retired: 'it has been retired from service.',
        unknown: 'it is not eligible.',
      },
      or: 'Or choose from stock',
      stockHeading: 'Available in your pharmacy',
      stockEmpty: 'There are no unassigned devices in stock. Scan or type a serial number instead.',
      firmware: (version: string) => `Firmware ${version}`,
      battery: (percent: number) => `Battery ${percent}%`,
      mains: 'On mains power',
      selected: 'Selected',
      useDevice: 'Use this device',
    },
    carers: {
      title: 'Add carers',
      intro:
        'Add the people who help the client with their medicines. Each carer is invited to the care app. You can continue without adding a carer.',
      heading: 'Carers',
      mobileLabel: 'Mobile number',
      mobilePlaceholder: '0491 570 156',
      mobileHint: 'Enter the mobile first. The carer signs in to the care app with this number.',
      mobileInvalid: 'Enter an Australian mobile number, such as 0491 570 156.',
      checking: 'Checking whether this person is already registered…',
      checkFailed: 'We couldn’t check this number.',
      matchTitle: (name: string) => `${name} is already registered`,
      matchBody: 'They will be linked to this client rather than registered again.',
      matchHasApp: 'They already use the care app, so they’ll be notified in the app.',
      matchNoApp: 'They’ll be sent an SMS invitation to the care app.',
      familyNameConfirm: (initial: string) =>
        `Enter their family name to confirm (it starts with ${initial}).`,
      familyNameMismatch: (initial: string) => `The registered family name starts with ${initial}.`,
      noMatchTitle: 'New carer',
      noMatchBody:
        'No one is registered with this mobile. Enter their details and they’ll be registered and sent an SMS invitation.',
      givenName: 'First name',
      familyName: 'Last name',
      givenNamePlaceholder: 'e.g. Emily',
      familyNamePlaceholder: 'e.g. Carter',
      relationship: 'Relationship to the client',
      relationshipPlaceholder: 'Select…',
      required: 'Required.',
      add: 'Add carer',
      duplicate: 'This carer has already been added.',
      clientMobile: 'This is the client’s own number. Use the client app option below instead.',
      unfinished: 'Add this carer, or clear the form, before continuing.',
      clear: 'Clear',
      added: (count: number) => `Added (${count})`,
      remove: (name: string) => `Remove ${name}`,
      matched: 'Already registered',
      created: 'New carer',
      footer:
        'Adding a carer links them to this client. If they’re already registered they are matched, not duplicated. All carers on a client have the same access.',
      appUserHeading: 'Client app access',
      appUserLabel: (name: string) => `Invite ${name} to use the care app for their own medicines`,
      appUserHint: 'They’ll see their own adherence and reminders. This is separate from carer access.',
      appUserMobile: 'Client’s mobile number',
      review: 'Review',
    },
    review: {
      title: 'Review and activate',
      intro: (name: string) =>
        `Check the details below. If everything is correct, confirm and activate ${name}’s MCM.`,
      client: 'Client',
      device: 'MCM device',
      deviceDetail: (firmware: string | null) =>
        firmware ? `Firmware ${firmware} · Valid device` : 'Valid device',
      doseWindows: (pack: string) => `Dose windows · Pack ${pack}`,
      doseWindowsNote:
        'Set from the pharmacy’s slot times in the MMS. The MCM monitors pack 1 only. Reminders are set by the client or carer in the app.',
      doseWindowsUnavailable: 'Dose windows could not be shown. They are set in the pharmacy’s MMS.',
      doseWindow: (from: string, to: string) => `${from} – ${to}`,
      carers: 'Carers',
      noCarers: 'None added',
      carerLine: (relationship: string, mobile: string) => `${relationship} · ${mobile}`,
      clientApp: 'Client app access',
      clientAppInvite: (mobile: string) => `Invite to the care app on ${mobile}`,
      clientAppNone: 'Not invited',
      subscriptionTitle: 'Confirm the MCM subscription and payment have been arranged.',
      subscriptionBody:
        'The Web MMS doesn’t process payment. Make sure this has been handled before activating.',
      notice: (name: string) =>
        `Activating links the MCM to ${name} and sends SMS or app invitations to the people listed. This action is logged.`,
      activate: (name: string) => `Activate MCM for ${name}`,
      activating: 'Activating…',
      failedTitle: 'The MCM was not activated',
      failedRetry: 'Nothing was changed. Check the details and try again.',
    },
    success: {
      title: 'MCM activated',
      body: (name: string, serial: string) => `${serial} is now linked to ${name}.`,
      active: 'The MCM is online and monitoring.',
      pending: 'Waiting for the MCM to report for the first time. This usually takes a few minutes.',
      invitesHeading: 'Invitations',
      noInvites: 'No invitations were sent. Carers can be added later.',
      matched: 'Linked to their existing registration',
      created: 'Registered as a new carer',
      clientApp: (name: string) => `${name} (client)`,
      channel: { sms: (to: string) => `SMS to ${to}`, push: 'Care app notification' },
      viewRecord: (name: string) => `View ${name}’s record`,
      another: 'Activate another MCM',
      dashboard: 'Go to the dashboard',
      activatedBy: (name: string, time: string) => `Activated by ${name} at ${time}`,
    },
  },
};

export type Copy = typeof en;
